import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

// Shared theme + building blocks for the Platform Analytics tabs.
// Mirrors the palette/styling used by CompanyInsightsPanel and the
// internal dashboards so the new page feels native.
export const T = {
  bg: "hsl(140, 10%, 95%)",
  bg2: "#ffffff",
  bg3: "hsl(140, 8%, 90%)",
  fg: "hsl(150, 30%, 12%)",
  primary: "hsl(150, 38%, 17%)",
  dim: "hsl(150, 10%, 46%)",
  border: "hsl(140, 8%, 88%)",
  green: "hsl(142, 71%, 45%)",
  red: "hsl(0, 72%, 51%)",
  blue: "hsl(217, 91%, 55%)",
  amber: "hsl(38, 92%, 50%)",
  teal: "hsl(174, 60%, 45%)",
  purple: "hsl(262, 65%, 55%)",
  orange: "hsl(24, 90%, 52%)",
};

// Stable color sequence for multi-series charts.
export const SERIES_COLORS = [T.primary, T.blue, T.amber, T.red, T.teal, T.purple, T.green];

export const cardSx = {
  border: `1px solid ${T.border}`,
  borderRadius: "0.75rem",
  backgroundColor: T.bg2,
  p: 2.5,
};

export const labelSx = {
  color: T.dim,
  fontSize: 12,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  mb: 0.5,
};

export const sectionLabelSx = { ...labelSx, fontSize: 13, mb: 0 };

export const chartTooltipStyle = {
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  fontFamily: "Segoe UI Regular, sans-serif",
  fontSize: 12,
};

export const axisTick = {
  fontSize: 11,
  fontFamily: "Segoe UI Regular, sans-serif",
  fill: T.dim,
};

export function SectionLabel({ children, note }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={sectionLabelSx}>{children}</Typography>
      {note && (
        <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, fontStyle: "italic" }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

// Single-metric card with optional comparison delta (mirrors DeltaBadge logic
// from CompanyInsightsPanel: "New" when no baseline, hidden when period=total).
export function MetricCard({ label, current, previous, comparisonLabel, comparable = true, suffix = "", barColor, scope }) {
  const hasComparison = current != null && previous != null && comparisonLabel != null;
  const diff = (current || 0) - (previous || 0);
  const up = diff >= 0;
  const hasBaseline = previous > 0;
  const pct = hasBaseline ? Math.round(Math.abs(diff / previous) * 100) : 0;
  const isNew = comparable && hasComparison && !hasBaseline && diff > 0;
  const showDelta = comparable && hasComparison && !isNew && pct > 0;

  return (
    <Box
      component={motion.div}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      sx={{
        border: `1px solid ${T.border}`,
        borderRadius: "0.75rem",
        overflow: "hidden",
        backgroundColor: T.bg2,
        "&:hover": { boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
      }}
    >
      <Box sx={{ height: 4, width: "100%", backgroundColor: barColor || (up ? T.green : T.red) }} />
      <Box sx={{ p: "1.25rem" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.75, mb: 1, minHeight: 30 }}>
          <Typography sx={{ color: T.dim, fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.3 }}>
            {label}
          </Typography>
          {scope && <ScopeChip scope={scope} />}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ color: T.fg, fontSize: 26, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", lineHeight: 1.2 }}>
            {current ?? 0}{suffix}
          </Typography>
          {isNew && <Badge up>New</Badge>}
          {showDelta && <Badge up={up}>{up ? "↑" : "↓"}{pct}% {comparisonLabel || ""}</Badge>}
        </Box>
      </Box>
    </Box>
  );
}

function Badge({ up, children }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        flexShrink: 0,
        backgroundColor: up ? "hsl(142 60% 92%)" : "hsl(0 70% 94%)",
        border: "1px solid",
        borderColor: up ? "hsl(142 60% 80%)" : "hsl(0 70% 80%)",
      }}
    >
      <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontWeight: 600, color: up ? "#079344" : "#D32F2F", whiteSpace: "nowrap" }}>
        {children}
      </Typography>
    </Box>
  );
}

export function ScopeChip({ scope }) {
  if (!scope) return null;
  return (
    <Box sx={{ px: 0.75, py: "1px", borderRadius: "5px", backgroundColor: "hsl(140 6% 93%)", border: "1px solid hsl(140 8% 86%)", flexShrink: 0 }}>
      <Typography sx={{ fontSize: 9.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: T.dim, lineHeight: 1.6, whiteSpace: "nowrap" }}>
        {scope}
      </Typography>
    </Box>
  );
}

export function StatTile({ label, value, suffix = "", warning }) {
  return (
    <Box
      sx={{
        ...cardSx,
        p: "1rem",
        ...(warning && { borderColor: "hsl(36 90% 70%)", backgroundColor: "hsl(36 90% 97%)" }),
      }}
    >
      <Typography sx={{ ...labelSx, mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ color: warning ? "#F57C00" : T.fg, fontSize: 22, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", lineHeight: 1.2 }}>
        {value ?? "—"}{value != null ? suffix : ""}
      </Typography>
    </Box>
  );
}

export function EmptyNote({ children = "No data available" }) {
  return (
    <Typography sx={{ fontSize: 13, color: T.dim, py: 4, textAlign: "center" }}>
      {children}
    </Typography>
  );
}
